import { isKnownEvent, type KnownEvent } from '../logging/events'
import type {
  LogCtx,
  LogEnv,
  LogSeverity,
  TelemetryLogEntry,
} from '../types/telemetry'
import { getPlatform } from './platform'
import { getActiveSessionId } from '../utils/debugSessionRecorder'
import { callable } from './callable'

const MAX_QUEUE_SIZE = 200
const FLUSH_BATCH_SIZE = 50
const FLUSH_THRESHOLD = 20
const FLUSH_INTERVAL_MS = 10_000

const clientSessionId =
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `client-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`

let userUid: string | null = null
let userWorkspaceId: string | null = null
let localDropCount = 0

const queue: TelemetryLogEntry[] = []
let flushTimer: ReturnType<typeof setTimeout> | null = null
let isFlushing = false

const getEnv = (): LogEnv => {
  const preview = import.meta.env.VITE_APP_PREVIEW_MODE
  if (preview === 'mock' || preview === 'debug') return preview
  if (import.meta.env.MODE === 'development') return 'debug'
  if (import.meta.env.MODE === 'production' || import.meta.env.PROD) return 'prod'
  return 'sit'
}

const shouldMirrorToConsole = () => {
  const env = getEnv()
  return env === 'debug' || env === 'mock' || import.meta.env.DEV
}

const scheduleTimer = () => {
  if (flushTimer || queue.length === 0) return
  flushTimer = setTimeout(() => {
    flushTimer = null
    void telemetryLogger.flush()
  }, FLUSH_INTERVAL_MS)
}

const clearTimer = () => {
  if (flushTimer) {
    clearTimeout(flushTimer)
    flushTimer = null
  }
}

const trimQueueIfFull = () => {
  if (queue.length < MAX_QUEUE_SIZE) return
  // Drop oldest debug/info first
  const dropIdx = queue.findIndex((e) => e.severity === 'debug' || e.severity === 'info')
  if (dropIdx !== -1) {
    queue.splice(dropIdx, 1)
    localDropCount += 1
  } else {
    // Drop oldest warn if all are warn/error
    queue.shift()
    localDropCount += 1
  }
}

const buildEntry = (
  severity: LogSeverity,
  event: KnownEvent,
  ctx?: LogCtx,
  error?: Error,
): TelemetryLogEntry => {
  const debugSessionId = getActiveSessionId()
  const route = typeof window !== 'undefined' ? window.location.pathname : ''
  const clientTs = new Date().toISOString()

  return {
    clientTs,
    severity,
    event,
    source: 'client',
    route,
    uid: userUid ?? undefined,
    workspaceId: userWorkspaceId ?? undefined,
    sessionId: clientSessionId,
    debugSessionId: debugSessionId ?? undefined,
    appVersion: import.meta.env.VITE_APP_VERSION ?? '0.1.0',
    buildId: import.meta.env.VITE_BUILD_ID ?? 'dev',
    platform: getPlatform(),
    env: getEnv(),
    errorCode: error && 'code' in error ? String((error as Record<string, unknown>).code) : undefined,
    errorMessage: error ? error.message.slice(0, 500) : undefined,
    errorStack: error && (severity === 'warn' || severity === 'error') ? (error.stack ?? '').slice(0, 4000) : undefined,
    ctx: ctx ? { ...ctx } : undefined,
  }
}

const pushEntry = (entry: TelemetryLogEntry) => {
  trimQueueIfFull()
  queue.push(entry)
  if (queue.length >= FLUSH_THRESHOLD || entry.severity === 'error') {
    void telemetryLogger.flush()
  } else {
    scheduleTimer()
  }
}

export interface TelemetryLogger {
  debug(event: KnownEvent, ctx?: LogCtx): void
  info(event: KnownEvent, ctx?: LogCtx): void
  warn(event: KnownEvent, ctx?: LogCtx, error?: Error): void
  error(event: KnownEvent, ctx?: LogCtx, error?: Error): void
  flush(): Promise<void>
  setUserContext(uid: string | null, workspaceId: string | null): void
  getLocalDropCount(): number
  _getQueueForTests(): TelemetryLogEntry[]
  _clearForTests(): void
  _enqueueRawForTests(entry: TelemetryLogEntry): void
}

export const telemetryLogger: TelemetryLogger = {
  debug(event: KnownEvent, ctx?: LogCtx) {
    if (!isKnownEvent(event)) {
      console.error('[telemetry] Rejected unknown event name:', event)
      return
    }
    const debugSessionId = getActiveSessionId()
    if (!debugSessionId) return // Drop debug when not in active debug session

    const entry = buildEntry('debug', event, ctx)
    pushEntry(entry)
  },

  info(event: KnownEvent, ctx?: LogCtx) {
    if (!isKnownEvent(event)) {
      console.error('[telemetry] Rejected unknown event name:', event)
      return
    }
    const debugSessionId = getActiveSessionId()
    if (!debugSessionId) return // Drop info when not in active debug session

    const entry = buildEntry('info', event, ctx)
    pushEntry(entry)
  },

  warn(event: KnownEvent, ctx?: LogCtx, error?: Error) {
    if (!isKnownEvent(event)) {
      console.error('[telemetry] Rejected unknown event name:', event)
      return
    }
    if (shouldMirrorToConsole()) {
      console.warn(`[telemetry:${event}]`, ctx, error)
    }
    const entry = buildEntry('warn', event, ctx, error)
    pushEntry(entry)
  },

  error(event: KnownEvent, ctx?: LogCtx, error?: Error) {
    if (!isKnownEvent(event)) {
      console.error('[telemetry] Rejected unknown event name:', event)
      return
    }
    if (shouldMirrorToConsole()) {
      console.error(`[telemetry:${event}]`, ctx, error)
    }
    const entry = buildEntry('error', event, ctx, error)
    pushEntry(entry)
  },

  async flush() {
    clearTimer()
    if (isFlushing || queue.length === 0) return
    isFlushing = true

    const batch = queue.splice(0, FLUSH_BATCH_SIZE)
    try {
      await callable<{ entries: TelemetryLogEntry[] }, { accepted: number; rejected: number }>(
        'ingestClientLogs',
        { entries: batch },
        { suppressLogging: true, timeoutMs: 15_000, retries: 1 }
      )
    } catch (err) {
      localDropCount += batch.length
      if (shouldMirrorToConsole()) {
        console.warn('[telemetry] Failed to ingest logs batch, dropped:', batch.length, err)
      }
    } finally {
      isFlushing = false
      if (queue.length > 0) {
        scheduleTimer()
      }
    }
  },

  setUserContext(uid: string | null, workspaceId: string | null) {
    userUid = uid
    userWorkspaceId = workspaceId
  },

  getLocalDropCount() {
    return localDropCount
  },

  _getQueueForTests() {
    return [...queue]
  },

  _clearForTests() {
    queue.length = 0
    clearTimer()
    isFlushing = false
    localDropCount = 0
  },

  _enqueueRawForTests(entry: TelemetryLogEntry) {
    trimQueueIfFull()
    queue.push(entry)
  },
}

// Module-level global error listeners
if (typeof window !== 'undefined') {
  window.addEventListener('error', (e) => {
    try {
      telemetryLogger.error(
        'ui.window.error',
        {
          filename: String(e.filename ?? '').slice(-120),
          lineno: Number(e.lineno ?? 0),
          colno: Number(e.colno ?? 0),
        },
        e.error instanceof Error ? e.error : new Error(String(e.message || 'Window error'))
      )
    } catch {
      // Prevent recursive errors in window error listener
    }
  })

  window.addEventListener('unhandledrejection', (e) => {
    try {
      const reason = e.reason instanceof Error ? e.reason : new Error(String(e.reason ?? 'Unhandled rejection'))
      telemetryLogger.error('ui.unhandled.rejection', {}, reason)
    } catch {
      // Prevent recursive errors
    }
  })

  const flushOnUnload = () => {
    if (queue.length === 0) return
    void telemetryLogger.flush()
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushOnUnload()
    }
  })
  window.addEventListener('pagehide', flushOnUnload)
  window.addEventListener('beforeunload', flushOnUnload)
}
